import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { createShareLink, listShareLinks, revokeShareLink } from '@/api/share';
import type { ShareLinkSummary } from '@/api/share';
import { usePlanGating } from '@/hooks/usePlanGating';
import { CollaboratorPanel } from '@/ui/CollaboratorPanel';
import { useIsMobile } from '@/hooks/useMediaQuery';

type TabId = 'link' | 'people';

interface ShareDialogProps {
  mapId: string;
  mapTitle: string;
  open: boolean;
  onClose: () => void;
  onOpenPricing?: () => void;
}

export function ShareDialog({ mapId, mapTitle, open, onClose, onOpenPricing }: ShareDialogProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { canCollaborate } = usePlanGating();
  const [activeTab, setActiveTab] = useState<TabId>('link');
  const [permission, setPermission] = useState<'view' | 'edit'>('view');
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [activeLinks, setActiveLinks] = useState<ShareLinkSummary[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [revokingToken, setRevokingToken] = useState<string | null>(null);

  // Load active share links
  const loadActiveLinks = useCallback(async () => {
    setLinksLoading(true);
    try {
      const resp = await listShareLinks(mapId);
      setActiveLinks(resp.links);
    } catch {
      // Silently fail — the list is supplementary
      setActiveLinks([]);
    } finally {
      setLinksLoading(false);
    }
  }, [mapId]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setShareUrl(null);
      setError(null);
      setCopied(false);
      setPermission('view');
      setLoading(false);
      setActiveTab('link');
      setRevokingToken(null);
      void loadActiveLinks();
    }
  }, [open, loadActiveLinks]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const handleCreateLink = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await createShareLink(mapId, permission);
      const origin = window.location.origin;
      setShareUrl(`${origin}?share=${resp.token}`);
      // Refresh the active links list
      void loadActiveLinks();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create share link';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [mapId, permission, loadActiveLinks]);

  const handleRevoke = useCallback(async (token: string) => {
    if (!window.confirm(t('dialogs.revokeConfirm'))) return;
    setRevokingToken(token);
    try {
      await revokeShareLink(mapId, token);
      setActiveLinks((prev) => prev.filter((l) => l.token !== token));
    } catch {
      // Ignore — user can retry
    } finally {
      setRevokingToken(null);
    }
  }, [mapId, t]);

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the input text
    }
  }, [shareUrl]);

  if (!open || !isAuthenticated) return null;

  // Gate edit-permission sharing behind the collaborate capability
  const editGated = !canCollaborate && permission === 'edit';

  function formatTimeRemaining(expiry: string): string {
    const diff = new Date(expiry).getTime() - Date.now();
    if (diff <= 0) return t('dialogs.expired');
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) {
      const mins = Math.max(1, Math.floor(diff / (1000 * 60)));
      return t('dialogs.expiresIn', { time: `${mins}m` });
    }
    if (hours < 24) return t('dialogs.expiresIn', { time: `${hours}h` });
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return t('dialogs.expiresIn', { time: remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d` });
  }

  const tabStyle = (tab: TabId): React.CSSProperties => ({
    flex: 1,
    padding: '8px 0',
    background: 'transparent',
    border: 'none',
    borderBottom: activeTab === tab ? '2px solid var(--color-primary)' : '2px solid transparent',
    color: activeTab === tab ? 'var(--text-primary)' : 'var(--text-tertiary)',
    cursor: 'pointer',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    fontFamily: 'var(--font-family)',
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-overlay)',
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-surface)',
          border: isMobile ? 'none' : '1px solid var(--border-default)',
          borderRadius: isMobile ? 0 : 'var(--radius-md)',
          padding: 24,
          minWidth: isMobile ? undefined : 400,
          maxWidth: isMobile ? undefined : 480,
          width: isMobile ? '100vw' : 440,
          height: isMobile ? '100vh' : undefined,
          margin: isMobile ? 0 : undefined,
          fontFamily: 'var(--font-family)',
          color: 'var(--text-primary)',
          maxHeight: isMobile ? undefined : '80vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-xl)', fontWeight: 600, color: 'var(--text-primary)' }}>
            {t('dialogs.shareMap')}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: 20,
              lineHeight: 1,
              padding: '0 4px',
            }}
            aria-label={t('common.close')}
          >
            X
          </button>
        </div>

        {/* Map title */}
        <p style={{ margin: '0 0 12px', fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)' }}>
          {mapTitle}
        </p>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid var(--border-default)' }}>
          <button onClick={() => setActiveTab('link')} style={tabStyle('link')}>
            {t('dialogs.shareLink')}
          </button>
          <button onClick={() => setActiveTab('people')} style={tabStyle('people')}>
            {t('dialogs.invitePeople')}
          </button>
        </div>

        {/* Share Link tab */}
        {activeTab === 'link' && (
          <>
            {/* Permission select */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 6 }}>
                {t('dialogs.permission')}
              </label>
              <select
                value={permission}
                onChange={(e) => setPermission(e.target.value as 'view' | 'edit')}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'var(--bg-app)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 6,
                  fontSize: 'var(--font-size-base)',
                  fontFamily: 'var(--font-family)',
                  outline: 'none',
                }}
              >
                <option value="view">{t('dialogs.canView')}</option>
                <option value="edit">{t('dialogs.canEdit')}</option>
              </select>
            </div>

            {/* Upgrade prompt for edit sharing on free plan */}
            {editGated && (
              <div
                style={{
                  padding: '12px 16px',
                  marginBottom: 16,
                  background: 'var(--bg-info)',
                  borderRadius: 6,
                  border: '1px solid var(--bg-info)',
                }}
              >
                <p style={{ margin: '0 0 8px', fontSize: 'var(--font-size-sm)', color: 'var(--text-primary)' }}>
                  {t('dialogs.upgradeToShare')}
                </p>
                <a
                  href="/pricing"
                  style={{
                    display: 'inline-block',
                    padding: '6px 14px',
                    background: 'var(--color-secondary)',
                    color: '#fff',
                    borderRadius: 6,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    textDecoration: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {t('common.upgrade')}
                </a>
              </div>
            )}

            {/* Error message */}
            {error && (
              <p style={{ margin: '0 0 12px', fontSize: 'var(--font-size-sm)', color: 'var(--color-danger)' }}>
                {error}
              </p>
            )}

            {/* Share URL display */}
            {shareUrl ? (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 6 }}>
                  {t('dialogs.shareLink')}
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="text"
                    readOnly
                    value={shareUrl}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      background: 'var(--bg-app)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-default)',
                      borderRadius: 6,
                      fontSize: 'var(--font-size-sm)',
                      fontFamily: 'var(--font-family)',
                      outline: 'none',
                    }}
                  />
                  <button
                    onClick={() => void handleCopy()}
                    style={{
                      background: 'var(--bg-surface-raised)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-strong)',
                      borderRadius: 6,
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-sm)',
                      fontFamily: 'var(--font-family)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {copied ? t('dialogs.copied') : t('dialogs.copy')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => void handleCreateLink()}
                disabled={loading || editGated}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  background: loading || editGated ? 'var(--border-strong)' : 'var(--color-primary)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: loading || editGated ? 'default' : 'pointer',
                  fontSize: 'var(--font-size-base)',
                  fontFamily: 'var(--font-family)',
                  fontWeight: 500,
                }}
              >
                {loading ? t('dialogs.creating') : t('dialogs.createLink')}
              </button>
            )}

            {/* Active Links Section */}
            <div style={{ marginTop: 20, borderTop: '1px solid var(--border-default)', paddingTop: 16 }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--text-secondary)' }}>
                {t('dialogs.activeLinks')}
              </h3>
              {linksLoading ? (
                <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-tertiary)' }}>
                  {t('dialogs.loadingLinks')}
                </p>
              ) : activeLinks.length === 0 ? (
                <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-tertiary)' }}>
                  {t('dialogs.noActiveLinks')}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {activeLinks.map((link) => (
                    <div
                      key={link.token}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 12px',
                        background: 'var(--bg-app)',
                        borderRadius: 6,
                        border: '1px solid var(--border-default)',
                        fontSize: 'var(--font-size-sm)',
                      }}
                    >
                      {/* Permission badge */}
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: 'var(--font-size-xs, 11px)',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          background: link.permission === 'edit' ? 'var(--color-warning, #f59e0b)' : 'var(--color-primary)',
                          color: '#fff',
                          flexShrink: 0,
                        }}
                      >
                        {link.permission === 'edit' ? t('dialogs.edit') : t('dialogs.view')}
                      </span>

                      {/* Time remaining */}
                      <span style={{ flex: 1, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {formatTimeRemaining(link.expiry)}
                      </span>

                      {/* Revoke button */}
                      <button
                        onClick={() => void handleRevoke(link.token)}
                        disabled={revokingToken === link.token}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--color-danger)',
                          cursor: revokingToken === link.token ? 'default' : 'pointer',
                          fontSize: 'var(--font-size-sm)',
                          fontFamily: 'var(--font-family)',
                          padding: '2px 6px',
                          opacity: revokingToken === link.token ? 0.5 : 1,
                          flexShrink: 0,
                        }}
                      >
                        {revokingToken === link.token ? t('dialogs.revoking') : t('dialogs.revoke')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Invite People tab */}
        {activeTab === 'people' && (
          <CollaboratorPanel mapId={mapId} onOpenPricing={onOpenPricing} />
        )}
      </div>
    </div>
  );
}
